import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import DOMPurify from 'dompurify'
import {
  Box,
  Text,
  Button,
  Group,
  Badge,
  Textarea,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  Select
} from '@mantine/core'
import {
  IoChatbubbleOutline,
  IoLockClosedOutline,
  IoEllipsisHorizontal,
  IoConstructOutline,
  IoChevronForwardOutline
} from 'react-icons/io5'
import { toast } from 'react-toastify'
import LoadingComponent from '../../../app/layout/LoadingComponents'
import { AmmoScoreBadge, POST_TYPE_LABELS, POST_TYPE_COLORS } from '../../../app/common/ui'
import { useStore } from '../../../app/stores/store'
import agent from '../../../app/api/agent'
import { FlagTargetType } from '../../../app/models/flag'
import type { PostCommentDto } from '../../../app/models/post'
import type { ContentPhotoDto } from '../../../app/models/contentPhoto'
import { ContentPhotoOwnerType } from '../../../app/models/contentPhoto'
import PhotoGallery from '../../builds/PhotoGallery'
import GalleryUploadButton from '../../builds/GalleryUploadButton'

function sanitizePostBody (body: string): string {
  if (!body) return ''
  const hasHtml = /<[a-z][\s\S]*>/i.test(body)
  const html = hasHtml ? body : body.replace(/\n/g, '<br />')
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a', 'span'],
    ALLOWED_ATTR: ['href', 'class', 'style']
  })
}

function readTimeMinutes (body: string): number {
  const words = body?.trim().split(/\s+/)?.length ?? 0
  return Math.max(1, Math.ceil(words / 200))
}

function getInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return displayName.slice(0, 2).toUpperCase()
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase()
  return '?'
}

const VOTE_BTN = {
  background: 'var(--gs-surface2)',
  border: '1.5px solid var(--gs-border-bright)',
  color: 'var(--gs-text)'
}

export default observer(function PostDetails () {
  const { blogStore, userStore, voteStore } = useStore()
  const {
    selectedPost: post,
    loadPost,
    loadComments,
    postComments,
    loadingComments,
    addComment,
    loadingInitial,
    deletePost,
    loading,
    updatePostVote
  } = blogStore
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const location = useLocation()
  const [commentBody, setCommentBody] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [displayScore, setDisplayScore] = useState(post?.voteScore ?? 0)
  const [displayVote, setDisplayVote] = useState<number | null>(post?.currentUserVote ?? null)
  const [replyingTo, setReplyingTo] = useState<PostCommentDto | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [reportReason, setReportReason] = useState('')
  const [reportCommentId, setReportCommentId] = useState<number | null>(null)
  const [submittingReport, setSubmittingReport] = useState(false)
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)

  const loadPhotos = async (postId: string) => {
    setPhotosLoading(true)
    try {
      const list = await agent.ContentPhotos.list(ContentPhotoOwnerType.Post, postId)
      setPhotos(list)
    } catch {
      setPhotos([])
    } finally {
      setPhotosLoading(false)
    }
  }
  const [commentSortBy, setCommentSortBy] = useState<string>('newest')

  useEffect(() => {
    if (id) loadPost(id)
    return () => blogStore.clearSelectedPost()
  }, [id, loadPost, blogStore])

  useEffect(() => {
    if (id) loadComments(id)
  }, [id, loadComments])

  useEffect(() => {
    if (post?.id) void loadPhotos(post.id)
  }, [post?.id])

  useEffect(() => {
    if (post) {
      setDisplayScore(post.voteScore ?? 0)
      setDisplayVote(post.currentUserVote ?? null)
    }
  }, [post?.id, post?.voteScore, post?.currentUserVote])

  const scrollToComments = useCallback(() => {
    const el = document.getElementById('post-comments')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    if (location.hash === '#comments') {
      const t = window.setTimeout(scrollToComments, 300)
      return () => window.clearTimeout(t)
    }
  }, [location.hash, scrollToComments, loadingComments, postComments.length])

  const handleDelete = () => {
    if (!post || !id) return
    if (window.confirm('Delete this post?')) {
      deletePost(id).then(() => history.push('/blog'))
    }
  }

  const handleAddComment = async () => {
    if (!id || !commentBody.trim()) return
    setSubmittingComment(true)
    try {
      await addComment(id, commentBody.trim(), replyingTo?.id ?? null)
      setCommentBody('')
      setReplyingTo(null)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleVote = async (value: number) => {
    if (!post || !id) return
    if (!userStore.isLoggedIn) {
      history.push('/register')
      return
    }
    const prevScore = displayScore
    const prevVote = displayVote
    const delta = value === displayVote ? -value : value - (displayVote ?? 0)
    setDisplayScore((s) => s + delta)
    setDisplayVote(value === displayVote ? null : value)
    updatePostVote(id, displayScore + delta, value === displayVote ? null : value)
    try {
      if (value === displayVote) {
        await voteStore.removeVote(0, id)
      } else {
        await voteStore.castVote(0, id, value)
      }
    } catch {
      setDisplayScore(prevScore)
      setDisplayVote(prevVote)
      updatePostVote(id, prevScore, prevVote)
    }
  }

  const openReportPost = () => {
    setReportCommentId(null)
    setReportReason('')
    setReportOpen(true)
  }

  const openReportComment = (commentId: number) => {
    setReportCommentId(commentId)
    setReportReason('')
    setReportOpen(true)
  }

  const submitReport = async () => {
    const reason = reportReason.trim()
    if (!reason) {
      toast.error('Please describe the issue.')
      return
    }
    if (!post) return
    setSubmittingReport(true)
    try {
      if (reportCommentId != null) {
        await agent.Flags.submit({
          targetType: FlagTargetType.PostComment,
          postCommentId: reportCommentId,
          reason
        })
      } else {
        await agent.Flags.submit({
          targetType: FlagTargetType.Post,
          targetId: post.id,
          reason
        })
      }
      toast.success('Report submitted. Moderators will review it.')
      setReportOpen(false)
      setReportReason('')
      setReportCommentId(null)
    } catch {
      // toast from interceptor
    } finally {
      setSubmittingReport(false)
    }
  }

  const isAuthor = userStore.user?.username === post?.authorUsername
  const canManagePost = isAuthor || userStore.isAdmin
  const canManageGallery = !!(post && (userStore.user?.username === post.authorUsername || userStore.isAdmin))

  const handleGalleryDelete = async (photoId: string) => {
    await agent.ContentPhotos.delete(photoId)
    setPhotos((prev) => prev.filter((p) => p.id !== photoId))
  }

  const handleGallerySetMain = async (photoId: string) => {
    await agent.ContentPhotos.setMain(photoId)
    if (post?.id) await loadPhotos(post.id)
  }

  const handleGalleryUploadSuccess = (photo: ContentPhotoDto) => {
    setPhotos((prev) => [...prev, photo])
  }

  const { topLevel, repliesByParent } = useMemo(() => {
    const top: PostCommentDto[] = []
    const byParent = new Map<number, PostCommentDto[]>()
    for (const c of postComments) {
      const pid = c.parentCommentId
      if (pid == null) top.push(c)
      else {
        const list = byParent.get(pid) ?? []
        list.push(c)
        byParent.set(pid, list)
      }
    }

    const compareFn = (a: PostCommentDto, b: PostCommentDto) => {
      switch (commentSortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'popular':
          return b.voteScore - a.voteScore || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
    }

    top.sort(compareFn)
    byParent.forEach((list) => list.sort(compareFn))
    return { topLevel: top, repliesByParent: byParent }
  }, [postComments, commentSortBy])

  const postAuthorId = post?.authorId

  const renderComment = (c: PostCommentDto, depth: number) => {
    const isOp = !!(postAuthorId && c.authorId && c.authorId === postAuthorId)
    const replies = repliesByParent.get(c.id) ?? []

    return (
      <Box key={c.id} mb="md" sx={{ marginLeft: depth > 0 ? 20 : 0, borderLeft: depth > 0 ? '2px solid var(--gs-border)' : undefined, paddingLeft: depth > 0 ? 12 : 0 }}>
        <Group align="flex-start" spacing="sm" noWrap position="apart">
          <Group align="flex-start" spacing="sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: c.authorImage ? `url(${c.authorImage}) center/cover` : 'var(--gs-surface2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--gs-text)',
                flexShrink: 0
              }}
            >
              {!c.authorImage && getInitials(c.authorDisplayName, c.authorUsername)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Group spacing="xs">
                <Text component={Link} to={`/profiles/${c.authorUsername}`} size="sm" weight={500} sx={{ color: 'var(--gs-text)' }}>
                  {c.authorDisplayName || c.authorUsername}
                </Text>
                {isOp && (
                  <Badge size="xs" variant="outline" color="orange" sx={{ textTransform: 'uppercase', fontSize: 9 }}>
                    OP
                  </Badge>
                )}
                <AmmoScoreBadge tier={c.authorAmmoTier ?? 'Newcomer'} score={c.authorAmmoScore ?? 0} size="sm" />
                <Text size="xs" color="dimmed">
                  {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                </Text>
              </Group>
              <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)', marginTop: 4 }}>
                {c.body}
              </Text>
              {userStore.isLoggedIn && depth === 0 && (
                <Button variant="subtle" size="xs" compact mt={4} onClick={() => setReplyingTo(c)}>
                  Reply
                </Button>
              )}
            </Box>
          </Group>
          {userStore.isLoggedIn && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon size="sm" variant="subtle" sx={{ color: 'var(--gs-muted)', flexShrink: 0 }} aria-label="Comment actions">
                  <IoEllipsisHorizontal size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => openReportComment(c.id)}>Report</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
        {replies.map((r) => renderComment(r, depth + 1))}
      </Box>
    )
  }

  if (loadingInitial || !post) {
    return <LoadingComponent content="Loading post..." />
  }

  const readTime = readTimeMinutes(post.body)

  const typeLabel = (POST_TYPE_LABELS[post.type] ?? 'Post').toUpperCase()
  const typeBadgeColor = post.type === 0 ? 'orange' : POST_TYPE_COLORS[post.type] ?? 'gray'

  return (
    <Box sx={{ maxWidth: 760, margin: '0 auto', padding: 24 }}>
      <Group position="apart" align="center" mb={10}>
        <Text component={Link} to="/blog" size="sm" sx={{ color: 'var(--gs-muted)' }}>
          ← Back to Blog
        </Text>
        <Group spacing="xs">
          {userStore.isLoggedIn && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm" sx={{ color: 'var(--gs-muted)' }} aria-label="Post actions">
                  <IoEllipsisHorizontal size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={openReportPost}>Report post</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
          <Badge
            size="sm"
            variant="filled"
            radius="sm"
            color={typeBadgeColor}
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              fontSize: 10
            }}
          >
            {typeLabel}
          </Badge>
        </Group>
      </Group>

      {post.coverImageUrl && (
        <Box
          component="img"
          src={post.coverImageUrl}
          alt=""
          onClick={() => setLightboxUrl(post.coverImageUrl!)}
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: 8,
            marginBottom: 10,
            cursor: 'zoom-in'
          }}
        />
      )}

      {!photosLoading && photos.length === 0 && canManageGallery && post && (
        <Box sx={{ marginBottom: 16 }}>
          <GalleryUploadButton
            ownerType={ContentPhotoOwnerType.Post}
            ownerId={post.id}
            canUpload
            onSuccess={handleGalleryUploadSuccess}
            label="+ Add Photos"
          />
        </Box>
      )}

      {(post.tags?.length ?? 0) > 0 && (
        <Group spacing="xs" mb={6}>
          {post.tags!.map((t) => (
            <Badge key={t.id} size="sm" variant="outline" radius="sm">
              {t.displayName || t.name}
            </Badge>
          ))}
        </Group>
      )}

      <Text
        component="h1"
        sx={{
          fontFamily: 'Oswald, sans-serif',
          fontWeight: 600,
          fontSize: 32,
          color: 'var(--gs-text)',
          marginTop: 0,
          marginBottom: 6,
          lineHeight: 1.15
        }}
      >
        {post.title}
      </Text>

      <Group spacing="sm" mb="sm">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: post.authorImage ? `url(${post.authorImage}) center/cover` : 'linear-gradient(135deg, #E85D04, #C9A84C)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'DM Sans, sans-serif'
          }}
        >
          {!post.authorImage && getInitials(post.authorDisplayName, post.authorUsername)}
        </Box>
        <Text component={Link} to={`/profiles/${post.authorUsername}`} weight={500} size="sm" sx={{ color: 'var(--gs-text)' }}>
          {post.authorDisplayName || post.authorUsername}
        </Text>
        <AmmoScoreBadge tier={post.authorAmmoTier ?? 'Newcomer'} score={post.authorAmmoScore ?? 0} size="sm" />
        <Text size="xs" color="dimmed">
          {format(new Date(post.createdAt), 'MMM d, yyyy')} · {readTime} min read
        </Text>
      </Group>

      {post.firearmModelId && post.firearmModelName && (
        <Box
          component={Link}
          to={{
            pathname: `/registry/${post.firearmModelId}`,
            state: {
              fromPost: true,
              postId: post.id,
              postTitle: post.title
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--gs-border)',
            background: 'var(--gs-card)',
            textDecoration: 'none',
            marginBottom: 16,
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: 'var(--gs-accent)' }
          }}
        >
          <IoConstructOutline size={22} color="var(--gs-muted)" style={{ flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Text size="xs" color="dimmed">
              Reviewed firearm
            </Text>
            <Text size="sm" weight={500} sx={{ color: 'var(--gs-text)' }}>
              {post.firearmModelName}
            </Text>
          </Box>
          <IoChevronForwardOutline size={18} color="var(--gs-muted)" style={{ flexShrink: 0 }} aria-hidden />
        </Box>
      )}

      {post.ammoProductId && post.ammoProductName && (
        <Box
          component={Link}
          to={`/ammo/${post.ammoProductId}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--gs-border)',
            background: 'var(--gs-card)',
            textDecoration: 'none',
            marginBottom: 16,
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: 'var(--gs-accent)' }
          }}
        >
          <IoConstructOutline size={22} color="var(--gs-muted)" style={{ flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Text size="xs" color="dimmed">
              Reviewed ammo
            </Text>
            <Text size="sm" weight={500} sx={{ color: 'var(--gs-text)' }}>
              {post.ammoProductName}
            </Text>
          </Box>
          <IoChevronForwardOutline size={18} color="var(--gs-muted)" style={{ flexShrink: 0 }} aria-hidden />
        </Box>
      )}

      <Group spacing="xs" mb="md">
        <ActionIcon
          size="md"
          variant="subtle"
          sx={{ ...VOTE_BTN, ...(displayVote === 1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
          onClick={() => handleVote(1)}
          aria-label="Upvote"
        >
          <Text weight={800} size="md" sx={{ lineHeight: 1 }}>
            +
          </Text>
        </ActionIcon>
        <Text sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{displayScore}</Text>
        <ActionIcon
          size="md"
          variant="subtle"
          sx={{ ...VOTE_BTN, ...(displayVote === -1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
          onClick={() => handleVote(-1)}
          aria-label="Downvote"
        >
          <Text weight={800} size="md" sx={{ lineHeight: 1 }}>
            −
          </Text>
        </ActionIcon>
        <Button
          component={Link}
          to={`/blog/${post.id}#comments`}
          variant="default"
          size="xs"
          leftIcon={<IoChatbubbleOutline size={14} />}
          sx={{
            marginLeft: 8,
            borderColor: 'var(--gs-border)',
            color: 'var(--gs-text)',
            fontFamily: 'DM Sans, sans-serif'
          }}
        >
          Comments ({postComments.length})
        </Button>
      </Group>

      {(photosLoading || photos.length > 0) && (
        <Box sx={{ marginBottom: 24 }}>
          {photosLoading
            ? (
            <Text size="sm" sx={{ color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}>
              Loading photos…
            </Text>
              )
            : photos.length > 0
              ? (
            <Box>
              <Group position="apart" align="center" mb="sm" sx={{ flexWrap: 'wrap' }}>
                <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--gs-text)' }}>
                  PHOTOS
                </Text>
                {canManageGallery && post && (
                  <GalleryUploadButton
                    ownerType={ContentPhotoOwnerType.Post}
                    ownerId={post.id}
                    canUpload={canManageGallery}
                    onSuccess={handleGalleryUploadSuccess}
                    label="+ Add Photos"
                  />
                )}
              </Group>
              <PhotoGallery
                variant="post"
                photos={photos}
                canManage={canManageGallery}
                onDelete={handleGalleryDelete}
                onSetMain={handleGallerySetMain}
                onImageClick={setLightboxUrl}
              />
            </Box>
                )
              : null}
        </Box>
      )}

      <Box
        sx={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 15,
          lineHeight: 1.8,
          color: 'var(--gs-text)'
        }}
        dangerouslySetInnerHTML={{ __html: sanitizePostBody(post.body) }}
      />

      {post.type === 1 && (
        <Box mt={28} sx={{
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {/* Rating bar */}
          {post.rating != null && (
            <Box sx={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--gs-border)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: 'var(--gs-surface)'
            }}>
              <Text sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 48,
                fontWeight: 700,
                lineHeight: 1,
                color: 'var(--gs-gold)',
                minWidth: 60
              }}>
                {post.rating}/5
              </Text>
              <Box>
                <Group spacing={3} mb={4}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Text key={i} component="span" sx={{ fontSize: 22, color: i <= (post.rating ?? 0) ? 'var(--gs-gold)' : 'var(--gs-border)' }}>
                      {i <= (post.rating ?? 0) ? '★' : '☆'}
                    </Text>
                  ))}
                </Group>
                <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                  Reviewer rating
                </Text>
              </Box>
            </Box>
          )}

          {/* Pros */}
          {post.pros && (
            <Box sx={{
              padding: '14px 20px',
              borderLeft: '3px solid var(--gs-green, #3FB950)',
              borderBottom: (post.cons || post.verdict) ? '1px solid var(--gs-border)' : undefined,
              background: 'rgba(63,185,80,0.04)'
            }}>
              <Text size="xs" weight={700} sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--gs-green, #3FB950)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6
              }}>
                Liked
              </Text>
              <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: 'var(--gs-text)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {post.pros}
              </Text>
            </Box>
          )}

          {/* Cons */}
          {post.cons && (
            <Box sx={{
              padding: '14px 20px',
              borderLeft: '3px solid var(--gs-red, #F85149)',
              borderBottom: post.verdict ? '1px solid var(--gs-border)' : undefined,
              background: 'rgba(248,81,73,0.04)'
            }}>
              <Text size="xs" weight={700} sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--gs-red, #F85149)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6
              }}>
                {'Didn\u2019t like'}
              </Text>
              <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: 'var(--gs-text)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {post.cons}
              </Text>
            </Box>
          )}

          {/* Verdict */}
          {post.verdict && (
            <Box sx={{
              padding: '14px 20px',
              background: 'rgba(201,168,76,0.08)'
            }}>
              <Text size="xs" weight={700} sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--gs-gold)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6
              }}>
                Verdict
              </Text>
              <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, fontWeight: 500, color: 'var(--gs-text)', lineHeight: 1.5 }}>
                {post.verdict}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {canManagePost && (
        <Group mt="lg" spacing="xs">
          {isAuthor && (
            <Button component={Link} to={`/manage/${post.id}`} variant="light" size="sm">
              Edit
            </Button>
          )}
          <Button color="red" variant="light" size="sm" onClick={handleDelete} loading={loading}>
            Delete
          </Button>
        </Group>
      )}

      <Box id="post-comments" sx={{ borderTop: '1px solid var(--gs-border)', marginTop: 32, paddingTop: 24, scrollMarginTop: 72 }}>
        <Group spacing="xs" mb={16} align="center">
          <IoChatbubbleOutline size={16} color="var(--gs-muted)" />
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 500, fontSize: 16 }}>
            COMMENTS ({postComments.length})
          </Text>
        </Group>

        <Box mb="md" sx={{ maxWidth: 200 }}>
          <Select
            label="Sort comments"
            placeholder="Select sort order"
            value={commentSortBy}
            onChange={(val) => setCommentSortBy(val || 'newest')}
            data={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
              { value: 'popular', label: 'Highest voted' }
            ]}
            sx={{
              '& .mantine-Select-input': {
                background: 'var(--gs-surface2)',
                borderColor: 'var(--gs-border-bright)',
                color: 'var(--gs-text)'
              },
              '& .mantine-Select-label': {
                color: 'var(--gs-text)',
                fontSize: 14,
                fontWeight: 500
              }
            }}
          />
        </Box>

        {userStore.isLoggedIn
          ? (
          <>
            {replyingTo && (
              <Text size="xs" color="dimmed" mb={6}>
                Replying to {replyingTo.authorDisplayName || replyingTo.authorUsername}{' '}
                <Text component="button" type="button" size="xs" color="orange" onClick={() => setReplyingTo(null)} sx={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Cancel
                </Text>
              </Text>
            )}
            <Textarea
              placeholder={replyingTo ? 'Write a reply…' : 'Add a comment…'}
              value={commentBody}
              onChange={(e) => setCommentBody(e.currentTarget.value)}
              minRows={2}
              mb="md"
              sx={{ fontFamily: 'DM Sans, sans-serif' }}
            />
            <Button color="ember" size="sm" onClick={handleAddComment} loading={submittingComment} disabled={!commentBody.trim()}>
              {replyingTo ? 'Post reply' : 'Post Comment'}
            </Button>
          </>
            )
          : (
          <Box
            sx={{
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8,
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 8
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <IoLockClosedOutline size={18} color="var(--gs-muted)" />
              <Box>
                <Text size="sm" weight={600} sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)' }}>
                  Join the conversation
                </Text>
                <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                  Create a free account to post comments and earn AmmoScore.
                </Text>
              </Box>
            </Box>
            <Group spacing="xs">
              <Button
                component={Link}
                to="/register"
                size="xs"
                sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}
              >
                Register
              </Button>
              <Button
                component={Link}
                to="/login"
                size="xs"
                variant="outline"
                sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)', fontFamily: 'DM Sans, sans-serif' }}
              >
                Sign in
              </Button>
            </Group>
          </Box>
            )}

        <Box mt="lg">
          {loadingComments
            ? (
            <Text size="sm" color="dimmed">
              Loading comments…
            </Text>
              )
            : postComments.length === 0
              ? (
            <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', fontStyle: 'italic' }}>
              No comments yet. Be the first to weigh in.
            </Text>
                )
              : (
                  topLevel.map((c) => renderComment(c, 0))
                )}
        </Box>
      </Box>

      <Modal
        opened={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        size="xl"
        padding={0}
        withCloseButton
        centered
        styles={{
          modal: { background: 'var(--gs-bg, #0d0d0d)', border: '1px solid var(--gs-border)' },
          close: { color: 'var(--gs-muted)', '&:hover': { background: 'var(--gs-surface2)' } }
        }}
      >
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt=""
            sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
          />
        )}
      </Modal>

      <Modal opened={reportOpen} onClose={() => setReportOpen(false)} title="Report to moderators" centered>
        <Text size="sm" color="dimmed" mb="sm">
          Describe what is wrong. Reports are reviewed by the moderation team.
        </Text>
        <TextInput
          placeholder="Reason"
          value={reportReason}
          onChange={(e) => setReportReason(e.currentTarget.value)}
          mb="md"
        />
        <Group position="right">
          <Button variant="default" onClick={() => setReportOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void submitReport()} loading={submittingReport}>
            Submit report
          </Button>
        </Group>
      </Modal>
    </Box>
  )
})
